import React, { useEffect, useState, useRef, useMemo } from "react";
import TextInput from "../../../components/elements/forms/TextInput";
import TextInputBox from "../../../components/elements/forms/TextInputBox";
import SubmitButton from "../../../components/elements/forms/SubmitButton";
import { useNavigate } from "react-router-dom";
import { useJob } from "../misc/useJob";
import SelectDropdown from "../../../components/elements/forms/Select";
import { PretestAss, SelectOption } from "../../../store/types/job";
import axiosInstance from "../../../utility/axiosInstance";
import { toast } from "sonner";
import { useFetchData } from "../../pretest-assessment/hooks/usePreAss";
import { useRubrics } from "../hooks/useRubrics";
import { Tooltip } from "antd";

const CreateJob: React.FC = () => {
  const navigate = useNavigate();
  const {
    data,
    error,
    loading,
    onChangeHandler,
    onChangeTextbox,
    onChangeSelect,
    onChangeQuestionSelect,
    updateQuestion,
    addQuestion,
    removeQuestion,
    updateAutonomousRubric,
    addAutonomousRubric,
    removeAutonomousRubric,
    onSubmit,
    culturalFitExists,
  } = useJob();

  const { rubrics } = useRubrics();

  const rubricOptions = useMemo(() => {
    return rubrics.map((r) => ({ id: r.id, name: r.name }));
  }, [rubrics]);
  // const [pretests, setPretests] = useState<IQuestionCreation[]>([]);
  // const [assessments, setAssessments] = useState<IQuestionCreation[]>([]);
  // const [loading2, setLoading] = useState(true);
  // const [error2, setError] = useState<Error | null>(null);
  const [generatingJD, setGeneratingJD] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onChangeTextboxRef = useRef(onChangeTextbox);
  const animationWordsRef = useRef<string[]>([]);
  const animationIndexRef = useRef<number>(0);
  const animationCurrentTextRef = useRef<string>("");

  useEffect(() => {
    onChangeTextboxRef.current = onChangeTextbox;
  }, [onChangeTextbox]);

  const { data: pretestData, fetchData } = useFetchData<PretestAss>(
    "/admin/organization/pretests",
  );
  const { data: assessmentData, fetchData2 } = useFetchData<PretestAss>(
    "/admin/organization/assessments",
  );

  // Fetch on mount
  useEffect(() => {
    if (fetchData) fetchData();
    if (fetchData2) fetchData2();
  }, [fetchData, fetchData2]);

  // Memoize transformed data
  const pretestOptions = useMemo<SelectOption[]>(() => {
    return (
      pretestData?.data?.map((pretest) => ({
        id: pretest.id,
        name: pretest.jobTitle,
      })) ?? []
    );
  }, [pretestData]);

  const assessmentOptions = useMemo<SelectOption[]>(() => {
    return (
      assessmentData?.data?.map((assessment) => ({
        id: assessment.id,
        name: assessment.jobTitle,
      })) ?? []
    );
  }, [assessmentData]);

  // Handle error display
  // if (error) {
  //   return <div className="text-red-600">Error: {error.message}</div>;
  // }

  // const getAssessments = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await axiosInstance.get<IQuestionCreation[]>(
  //       // "/admin/organization/assessments"
  //       "/admin/all-assessments"
  //     );
  //     setAssessments(response.data);
  //   } catch (err) {
  //     setError(err instanceof Error ? err : new Error("An error occurred"));
  //     console.error("Error fetching jobs:", err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   // getPretests();
  //   getAssessments();
  // }, []);

  // Cleanup animation interval on unmount
  useEffect(() => {
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, []);

  const generateJobDescription = async () => {
    // Validate that job title is entered
    if (!data.title || data.title.trim().length < 3) {
      toast.error("Please enter a job title first");
      return;
    }
    if (!data.context || data.context.trim().length < 3) {
      toast.error("Please enter a job context first");
      return;
    }

    setGeneratingJD(true);
    setIsAnimating(false);

    try {
      // Call the real API endpoint
      const response = await axiosInstance.get(
        "/job/generate/job-description",
        { params: { jobTitle: data.title, context: data.context } },
      );

      // Extract the generated description from the response
      const generatedDescription =
        response.data?.data || response.data?.description || "";

      if (!generatedDescription) {
        throw new Error("No job description generated from the API");
      }

      // Start animating the text
      animateText(generatedDescription);
    } catch (error: any) {
      console.error("Error generating job description:", error);

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate job description";

      toast.error(errorMessage);
      setGeneratingJD(false);
    }
  };

  const animateText = (fullText: string) => {
    setIsAnimating(true);

    // Reset animation refs
    animationWordsRef.current = fullText.split(" ");
    animationIndexRef.current = 0;
    animationCurrentTextRef.current = "";

    // Clear any existing interval
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }

    // Update description field with animated text using refs to avoid stale closures
    const interval = setInterval(() => {
      const words = animationWordsRef.current;
      const totalWords = words.length;
      const currentIndex = animationIndexRef.current;

      if (currentIndex < totalWords) {
        animationCurrentTextRef.current +=
          (currentIndex > 0 ? " " : "") + words[currentIndex];

        // Update the description field using ref callback to avoid stale closure
        const syntheticEvent = {
          target: {
            name: "description",
            value: animationCurrentTextRef.current,
          },
        } as React.ChangeEvent<HTMLTextAreaElement>;

        onChangeTextboxRef.current(syntheticEvent);
        animationIndexRef.current++;
      } else {
        clearInterval(interval);
        setIsAnimating(false);
        setGeneratingJD(false);
        animationIntervalRef.current = null;
      }
    }, 20); // Adjust speed here (lower = faster)

    animationIntervalRef.current = interval;
  };

  return (
    <div className="w-full py-5">
      <div className="max-w-[1096px] mx-auto px-3">
        <div className="flex border-b border-border text-20">
          <div
            onClick={() => navigate(`/create-job`)}
            className="border-b-[3px] border-secondary pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[600]">Add Job</p>
          </div>
          <div
            onClick={() => navigate(`/create-interview`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[400]">Create Interview</p>
          </div>
          {/* <div
            onClick={() => navigate(`/create-bulk-candidates`)}
            className="pb-1 cursor-pointer"
          >
            <p className="font-[400]">Add Multiple Interview</p>
          </div> */}
        </div>
        <div className="rounded-10 bg-white px-4 py-5 mt-5">
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <TextInput
              label="Job Title"
              placeholder="Enter job title"
              name="title"
              value={data.title}
              error={error.title}
              onChange={onChangeHandler}
              required={true}
              min={3}
            />
            <TextInput
              label="Job Context"
              placeholder="Enter job context"
              name="context"
              value={data.context}
              // error={error.context}
              onChange={onChangeHandler}
              min={3}
            />
          </div>
          <div className="mb-3">
            <div className="flex justify-between items-end mb-1">
              <label className="text-[#1E1E1E] text-[15px]">
                Job Description <span className="text-[red]">*</span>
              </label>
              <button
                type="button"
                onClick={generateJobDescription}
                disabled={
                  generatingJD ||
                  isAnimating ||
                  !data.title?.trim() ||
                  !data.context?.trim()
                }
                className={`px-4 py-2 text-sm font-abel rounded-md border transition-all duration-200 ${
                  generatingJD ||
                  isAnimating ||
                  !data.title?.trim() ||
                  !data.context?.trim()
                    ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-300 text-gray-500"
                    : "bg-secondary text-white border-secondary hover:bg-[#1e2a5e] hover:border-[#1e2a5e] cursor-pointer"
                }`}
              >
                {generatingJD || isAnimating ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isAnimating ? "Generating..." : "Loading..."}
                  </span>
                ) : (
                  "Auto Generate JD"
                )}
              </button>
            </div>
            <TextInputBox
              rows={4}
              placeholder="Enter job description or click 'Auto Generate JD' to generate automatically"
              name="description"
              value={data.description}
              error={error.description}
              onChange={onChangeTextbox}
              required={true}
              min={3}
            />
          </div>
          <div className="mb-3 flex flex-col gap-2">
            <SelectDropdown
              items={[
                { id: "AUTONOMOUS", name: "Autonomous Interview" },
                { id: "GUIDED", name: "Guided Interview" },
                { id: "MIXED", name: "Hybrid Interview" },
              ]}
              // id="mode-select"
              name="interviewMode"
              label="Interview Mode"
              value={data.interviewMode || ""}
              onChange={onChangeSelect}
              required={true}
            />
            {/* <div>
              <label
                htmlFor="mode-select"
                className="font-[600] block text-[#1E1E1E] text-[15px] capitalize mb-1"
              >
                Select Interview Mode
              </label>
              <select
                id="mode-select"
                value={data.interviewMode}
                onChange={onChangeSelect}
                required={true}
                name="interviewMode"
                className="w-full px-3 py-3 border border-[rgb(232,232,232)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="" disabled>
                  Choose a mode...
                </option>
                <option value="AUTONOMOUS">Autonomous Interview</option>
                <option value="GUIDED">Guided Interview</option>
                <option value="MIXED">Hybrid Interview</option>
              </select>
            </div> */}
            {(data.interviewMode === "MIXED" ||
              data.interviewMode === "AUTONOMOUS") && (
              <div>
                <label
                  htmlFor="mode-select"
                  className="font-[600] block text-[#1E1E1E] text-[15px] capitalize mb-1"
                >
                  Select Amounts Of Questions Asked By AI
                </label>
                <select
                  id="maxAiQuestions"
                  value={data.maxAiQuestions}
                  onChange={onChangeQuestionSelect}
                  name="maxAiQuestions"
                  className="w-full px-3 py-3 border border-[rgb(232,232,232)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="" disabled>
                    Choose the number of question you wnat the AI to ask.....
                  </option>
                  {[...Array(15)].map((_, index) => (
                    <option value={index + 1} key={index}>
                      {index + 1}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {(data.interviewMode === "AUTONOMOUS" ||
            data.interviewMode === "MIXED") && (
            <div className="mb-5 p-4 bg-[#F9FAFB] rounded-10 border border-[#E5E7EB]">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-18 font-[600] text-secondary">
                  Autonomous Evaluation Rubrics
                </h3>
                {/* <button
                  type="button"
                  onClick={addAutonomousRubric}
                  className="px-3 py-1 text-sm bg-secondary text-white rounded-md hover:bg-opacity-90"
                >
                  + Add Rubric
                </button> */}
              </div>
              <div className="flex flex-col gap-2">
                {data.autonomousRubrics?.map((rubric, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end pb-3 border-b border-gray-100 last:border-0"
                  >
                    <div className="md:col-span-8">
                      <TextInput
                        label={index === 0 ? "Competency" : ""}
                        placeholder="e.g. Technical Skills"
                        name={`autonomous-competency-${index}`}
                        value={rubric.competency}
                        onChange={(e) =>
                          updateAutonomousRubric(
                            index,
                            "competency",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="md:col-span-3">
                      <SelectDropdown
                        label={index === 0 ? "Weight" : ""}
                        items={[
                          { id: 1, name: "1 (Low)" },
                          { id: 2, name: "2 (Medium)" },
                          { id: 3, name: "3 (High)" },
                          { id: 4, name: "4 (Higher)" },
                          { id: 5, name: "5 (Highest)" },
                        ]}
                        name={`autonomous-weight-${index}`}
                        value={rubric.weight}
                        onChange={(e) =>
                          updateAutonomousRubric(
                            index,
                            "weight",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    {/* <div className="md:col-span-1 pb-1">
                      <button
                        type="button"
                        onClick={() => removeAutonomousRubric(index)}
                        className="text-red-500 hover:text-red-700 p-2"
                        title="Remove Rubric"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div> */}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(data.interviewMode === "GUIDED" ||
            data.interviewMode === "MIXED" ||
            data.interviewMode === "") &&
            data.interviewQuestions.map((interview, index) => (
              <div
                key={index}
                className="w-full grid grid-cols-1 md:grid-cols-4 gap-3 border-b border-primary mb-2 pb-2"
              >
                <div className="md:col-span-3">
                  <TextInputBox
                    rows={4}
                    showIcon={true}
                    label={`Interview Questions ${index + 1}`}
                    placeholder="Enter question"
                    name={`question-${index}`}
                    value={interview.question}
                    status={error.interviewQuestions}
                    onChange={(e) => {
                      updateQuestion(index, "question", e.target.value);
                    }}
                    add={addQuestion}
                    required={true}
                    remove={
                      data.interviewQuestions.length > 1
                        ? () => removeQuestion(index)
                        : undefined
                    }
                  />
                </div>
                <div className="md:col-span-1">
                  <SelectDropdown
                    label="Assign Competence"
                    items={[...rubricOptions, { id: "custom", name: "Custom" }]}
                    value={interview.rubricId || ""}
                    name={`rubric-${index}`}
                    onChange={(e) => {
                      updateQuestion(index, "rubricId", e.target.value);
                    }}
                  />
                  {interview.rubricId === "custom" && (
                    <div>
                      <TextInput
                        label="Custom Title"
                        placeholder="Enter custom title"
                        name="competency"
                        value={interview.competency}
                        onChange={(e) => {
                          updateQuestion(index, "competency", e.target.value);
                        }}
                        min={3}
                      />
                    </div>
                  )}
                  <div>
                    <SelectDropdown
                      items={[
                        { id: 1, name: "1" },
                        { id: 2, name: "2" },
                        { id: 3, name: "3" },
                        { id: 4, name: "4" },
                        { id: 5, name: "5" },
                      ]}
                      name="weight"
                      label="Question Importance"
                      value={interview.weight || ""}
                      onChange={(e) => {
                        updateQuestion(index, "weight", e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

          {!culturalFitExists && (
            <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <div className="md:col-span-3">
                <label className="text-[#1E1E1E] text-[15px] block">
                  Cultural Fit
                </label>
                {/* <Tooltip title="Enter organization's cultural fit values (e.g. We value innovation, collaboration...)">
                <InfoCircleOutlined />
              </Tooltip> */}
                <TextInputBox
                  rows={3}
                  placeholder="Enter organization's cultural fit values (e.g. We value innovation, collaboration...)"
                  name="culturalFit"
                  value={data.culturalFit || ""}
                  onChange={onChangeTextbox}
                />
                {/* <p className="text-[13px] text-gray-500 italic -mt-3">
                  Note: This description will be saved for your organization and
                  hidden in future job creations.
                </p> */}
              </div>
              <div>
                <SelectDropdown
                  items={[
                    { id: 1, name: "1" },
                    { id: 2, name: "2" },
                    { id: 3, name: "3" },
                    { id: 4, name: "4" },
                    { id: 5, name: "5" },
                  ]}
                  name="culturalFitWeight"
                  label="Weight"
                  value={data.culturalFitWeight || ""}
                  onChange={onChangeSelect}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
            <div className="col-span-1 md:col-span-1">
              <SelectDropdown
                items={pretestOptions}
                // items={pretests?.map((pretest) => ({
                //   id: pretest?.id,
                //   name: pretest?.jobTitle,
                // }))}
                name="pretestId"
                label="Pretest"
                value={data.pretestId || ""}
                onChange={onChangeSelect}
              />
            </div>

            <div className="col-span-1 md:col-span-1">
              <SelectDropdown
                items={assessmentOptions}
                // items={assessments?.map((assessment) => ({
                //   id: assessment?.id,
                //   name: assessment?.jobTitle,
                // }))}
                name="assessmentId"
                label="Assessment"
                value={data.assessmentId || ""}
                onChange={onChangeSelect}
              />
            </div>
            {/* <div className="col-span-1 pt-4 ml-auto">
              <div
                className="h-[47px] w-[180px] flex items-center justify-center bg-secondary text-white rounded-8 ml-3 cursor-pointer"
                onClick={() => navigate(`/pretest-create`)}
              >
                Add New
              </div>
            </div> */}
          </div>
          <div className="h-[20px]"></div>

          <SubmitButton
            title="Create Job"
            onSubmitHandler={onSubmit}
            // onSubmitHandler={() => navigate(`/create-interview`)}
            loading={loading}
            // type="submit"
            className="font-[400] text-white md:hover:bg-[white] md:hover:text-secondary hover:border-primary"
          />
        </div>
      </div>
    </div>
  );
};

export default CreateJob;
